'use client';

import { useLoadableState } from '@carvana/merch-react-hooks';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import { useUserLocation } from '~/context/common/UserContext';
import ParentModelContext, {
  defaultParentModelContext
} from '~/context/new-cars/ParentModelContext';
import type {
  AutoSelectKey,
  CustomizeRequest,
  CustomizeResponse,
  PendingTrigger
} from '~/contracts/new-cars';
import {
  addToSelection,
  removeFromSelection
} from '~/hooks/new-cars/autoSelectUtils';
import useGetCustomizeOptions from '~/hooks/new-cars/services/useGetCustomizeOptions';
import useAutoSelect from '~/hooks/new-cars/useAutoSelect';
import AutoSelectModalWrapper from './AutoSelectModalWrapper';

type AutoSelectContextType = {
  setPendingOptionDeselection: (key: AutoSelectKey, code: string) => void;
  setPendingOptionSelection: (key: AutoSelectKey, code: string) => void;
  trimLevelSelections: Partial<CustomizeRequest>;
};

const AutoSelectContext = createContext<AutoSelectContextType>({
  setPendingOptionDeselection: () => {},
  setPendingOptionSelection: () => {},
  trimLevelSelections: {}
});

export function AutoSelectContextProvider({ children }: PropsWithChildren) {
  const pendingCustomizeOptions = useLoadableState<CustomizeResponse>(
    defaultParentModelContext.customizeOptions.data
  );

  const [pendingSelectedOptions, setPendingSelectedOptionsRaw] =
    useState<CustomizeRequest | null>(null);
  const [pendingTrigger, setPendingTrigger] = useState<PendingTrigger | null>(
    null
  );

  const {
    customizeOptions,
    resetSelectedOptions,
    selectedOptions,
    selectedTrim,
    selectedTruckBodyStyle,
    setSelectedOptions,
    suppressNextDisplayFetchRef
  } = useContext(ParentModelContext);

  const acceptPending = useCallback(
    (finalState: CustomizeRequest) => {
      suppressNextDisplayFetchRef.current = true;
      customizeOptions.setData.call(null, pendingCustomizeOptions.data);
      setSelectedOptions(finalState);
      setPendingSelectedOptionsRaw(null);
      setPendingTrigger(null);
    },
    [
      customizeOptions.setData,
      pendingCustomizeOptions.data,
      setSelectedOptions,
      suppressNextDisplayFetchRef
    ]
  );

  const clearPending = useCallback(() => {
    setPendingSelectedOptionsRaw(null);
    setPendingTrigger(null);
  }, []);

  const { zip } = useUserLocation();

  const trimLevelSelectionsRef = useRef<Partial<CustomizeRequest>>({});

  const setPendingOptionSelection = useCallback(
    (key: AutoSelectKey, code: string) => {
      if (!selectedOptions) return;

      const count = (() => {
        switch (key) {
          case 'driveType':
            return (
              customizeOptions.data.driveTypes.find(d => d.name === code)
                ?.count ?? 0
            );
          case 'engine':
            return (
              customizeOptions.data.engines.find(e => e.code === code)?.count ??
              0
            );
          case 'exterior':
            return (
              customizeOptions.data.exteriorColors.find(c => c.code === code)
                ?.count ?? 0
            );
          case 'interior':
            return (
              customizeOptions.data.interiorColors.find(c => c.code === code)
                ?.count ?? 0
            );
          case 'package':
            return (
              customizeOptions.data.packageOptions.find(p => p.code === code)
                ?.count ?? 0
            );
        }
      })();

      if (count === 0) {
        if (!selectedTrim) return;

        setPendingTrigger({ code, key, type: 'unavailable' });
        setPendingSelectedOptionsRaw(
          addToSelection(
            {
              ...(selectedTruckBodyStyle ?? selectedTrim),
              ...trimLevelSelectionsRef.current,
              zipcode: zip
            },
            key,
            code
          )
        );
      } else {
        setPendingTrigger({ code, key, type: 'select' });
        setPendingSelectedOptionsRaw(
          addToSelection(selectedOptions, key, code)
        );
      }
    },
    [
      customizeOptions.data,
      selectedOptions,
      selectedTrim,
      selectedTruckBodyStyle,
      zip
    ]
  );

  const setPendingOptionDeselection = useCallback(
    (key: AutoSelectKey, code: string) => {
      if (!selectedOptions) return;

      setPendingTrigger({ code, key, type: 'deselect' });
      setPendingSelectedOptionsRaw(
        removeFromSelection(selectedOptions, key, code)
      );
    },
    [selectedOptions]
  );

  useGetCustomizeOptions({
    loadableState: pendingCustomizeOptions,
    request: pendingSelectedOptions
  });

  const { confirm, handleCancel, handleContinue, trimLevelSelections } =
    useAutoSelect({
      acceptPending,
      clearPending,
      customizeOptions,
      pendingCustomizeOptions,
      pendingSelectedOptions,
      pendingTrigger,
      resetSelectedOptions,
      selectedOptions,
      setSelectedOptions
    });

  trimLevelSelectionsRef.current = trimLevelSelections;

  const value = useMemo<AutoSelectContextType>(
    () => ({
      setPendingOptionDeselection,
      setPendingOptionSelection,
      trimLevelSelections
    }),
    [
      setPendingOptionDeselection,
      setPendingOptionSelection,
      trimLevelSelections
    ]
  );

  return (
    <AutoSelectContext.Provider value={value}>
      <AutoSelectModalWrapper
        confirm={confirm}
        onCancel={handleCancel}
        onContinue={handleContinue}
      />
      {children}
    </AutoSelectContext.Provider>
  );
}

AutoSelectContextProvider.displayName = 'AutoSelectContextProvider';

export default AutoSelectContext;
